import React, { useState, useMemo, useRef, useEffect, Suspense } from 'react';
import { Link, useLocation, useNavigate, Outlet } from 'react-router-dom';
import PageTransition from './PageTransition';
import MobileTabBar from './MobileTabBar';
import MobileMoreSheet from './MobileMoreSheet';
import { navSections, navItemDe, TITRES_HORS_MENU } from './navItems';
import Nouveautes from './Nouveautes';
import { useData } from '@/lib/dataContext';
import { useAuth } from '@/lib/AuthContext';
import { LicorneDecor, THEMES } from '@/components/renouvellements/ThemeLicorne';
import { RefreshCw, LogOut, KeyRound } from 'lucide-react';

/** Pastilles de synchro de la barre du haut, dans cet ordre exact. */
const BOARDS_TOPBAR = ['webforce', 'digicommerce', 'coldcall'];

const DOT_TONE = { ok: 'bg-[#10B981]', syncing: 'bg-[#F59E0B]', error: 'bg-rose', pending: 'bg-handle' };

/** Carré « WF » : la marque, sans dégradé. */
export function BrandMark({ size = 32 }) {
  return (
    <span
      className="inline-flex items-center justify-center flex-shrink-0 bg-primary text-white font-extrabold"
      style={{ width: size, height: size, borderRadius: size >= 32 ? 10 : 9, fontSize: size >= 32 ? 12 : 11 }}
      aria-hidden="true"
    >
      WF
    </span>
  );
}

function boardStatus(row) {
  if (!row) return 'pending';
  if (row.status === 'success' || row.status === 'ok') return 'ok';
  if (row.status === 'running') return 'syncing';
  return row.status === 'error' ? 'error' : 'pending';
}

function UserMenu({ user, isAdmin, initials, onLogout, className = '' }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onClick);
    window.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClick);
      window.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <div className={`relative ${className}`} ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-label="Menu utilisateur"
        aria-expanded={open}
        className="w-11 h-11 -m-1 rounded-full flex items-center justify-center appui"
      >
        <span className="w-9 h-9 rounded-full bg-primary-soft text-primary-text font-extrabold text-[13px] flex items-center justify-center" aria-hidden="true">
          {initials}
        </span>
      </button>

      {open && (
        <div className="absolute right-0 top-[calc(100%+8px)] z-50 w-72 rounded-xl bg-card border border-border overflow-hidden motion-montee" role="menu">
          <div className="flex items-center gap-3 px-4 py-3.5 border-b border-muted">
            <span className="w-10 h-10 rounded-full bg-primary-soft text-primary-text font-extrabold text-sm flex items-center justify-center flex-shrink-0">
              {initials}
            </span>
            <div className="min-w-0 flex-1">
              <div className="text-sm font-extrabold truncate">{user?.full_name || user?.email}</div>
              <div className="text-[13px] text-foreground-secondary truncate">{user?.email}</div>
            </div>
            {isAdmin && (
              <span className="tag tag-primary flex-shrink-0">Admin</span>
            )}
          </div>
          <Link
            to="/mon-compte"
            onClick={() => setOpen(false)}
            className="w-full flex items-center gap-2.5 px-4 min-h-[48px] text-sm font-semibold hover:bg-surface-hover"
            role="menuitem"
          >
            <KeyRound className="w-4 h-4 text-foreground-secondary" />
            Mon compte · mot de passe
          </Link>
          <button
            type="button"
            onClick={() => { setOpen(false); onLogout(); }}
            className="w-full flex items-center gap-2.5 px-4 min-h-[48px] text-sm font-semibold hover:bg-surface-hover"
            role="menuitem"
          >
            <LogOut className="w-4 h-4 text-foreground-secondary" />
            Se déconnecter
          </button>
        </div>
      )}
    </div>
  );
}

export default function AppLayout() {
  const location = useLocation();
  const navigate = useNavigate();
  const { loading, syncing, anyRunning, triggerSync, lastFetch, syncStatuses } = useData();
  const isSyncing = syncing || anyRunning;
  const { user, logout } = useAuth();
  const rawRole = user?.role || user?.app_role || user?.data?.role || '';
  const isAdmin = rawRole === 'admin';
  const permittedPages = Array.isArray(user?.permitted_pages) ? user.permitted_pages : null;
  // L'état des synchros Monday (leads) ne parle qu'à ceux qui pilotent tout le tableau de bord :
  // une équipe à accès restreint (recouvrement, renouvellement) n'en a que l'inquiétude.
  const voitSynchro = isAdmin || !permittedPages;
  // Accès à une entrée de navigation :
  // - admin : tout ;
  // - liste permitted_pages : uniquement ces chemins ;
  // - par défaut : tout ce qui n'est pas réservé à l'admin.
  const canSeeNavItem = (item) => {
    if (isAdmin) return true;
    if (permittedPages) return permittedPages.includes(item.path);
    return !item.adminOnly;
  };

  // Mode « kiosque » : une seule page autorisée → ni barre latérale ni barre
  // du haut, la page en plein écran + menu utilisateur discret pour se déconnecter.
  const isKiosk = !isAdmin && permittedPages && permittedPages.length === 1;

  // Page hors de permitted_pages → première page autorisée. Sinon les pages
  // Executive/Salesperson/… plantent (RLS bloque les données) → écran blanc
  // pour la renouveleuse sur iPad. Idempotent, rejoué à chaque changement de chemin.
  useEffect(() => {
    if (isAdmin) return;
    if (!permittedPages || permittedPages.length === 0) return;
    if (location.pathname === '/mon-compte') return; // ouvert à tous les connectés
    const allowed = permittedPages.some(p =>
      location.pathname === p || location.pathname.startsWith(p + '/')
    // Les vues Renouvellements et Upsell ouvrent la fiche client : elles y donnent accès
    // (l'API ne laisse ouvrir à l'Upsell que les clients de sa liste).
    ) || (['/renouvellements', '/upsell'].some((p) => permittedPages.includes(p)) && location.pathname.startsWith('/clients/'));
    if (!allowed) {
      navigate(permittedPages[0], { replace: true });
    }
  }, [location.pathname, isAdmin, permittedPages, navigate]);

  const [moreSheetOpen, setMoreSheetOpen] = useState(false);
  useEffect(() => { setMoreSheetOpen(false); }, [location.pathname]);

  const userInitials = useMemo(() => {
    const name = user?.full_name || user?.email || '';
    const parts = name.split(/[\s@.]+/).filter(Boolean).slice(0, 2);
    return parts.map(s => s[0]?.toUpperCase()).join('') || '?';
  }, [user]);

  const overallStatus = useMemo(() => {
    if (syncStatuses.some(s => s.status === 'error')) return 'error';
    if (isSyncing || syncStatuses.some(s => s.status === 'running')) return 'syncing';
    if (syncStatuses.some(s => s.status === 'success' || s.status === 'ok')) return 'ok';
    return 'pending';
  }, [syncStatuses, isSyncing]);


  const syncLabel = isSyncing ? 'Synchronisation…' : overallStatus === 'ok' ? 'Synchronisé' : overallStatus === 'error' ? 'Erreur de synchro' : 'En attente';
  const syncTime = lastFetch ? lastFetch.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' }) : null;
  const sync = { status: overallStatus, label: syncLabel, time: syncTime, isSyncing, trigger: triggerSync };

  const sections = navSections(canSeeNavItem);
  const currentNav = navItemDe(location.pathname);
  const licorne = user?.renouv_theme === THEMES.LICORNE;
  const prenom = (user?.full_name || '').split(' ')[0];

  if (isKiosk) {
    return (
      <div className="min-h-screen bg-background relative">
        <div className="absolute top-2 right-2 z-50">
          <UserMenu user={user} isAdmin={isAdmin} initials={userInitials} onLogout={logout} />
        </div>
        <main className="min-h-screen">
          <PageTransition>
            <Suspense fallback={<div className="skeleton h-28 rounded-xl" aria-busy="true" />}><Outlet /></Suspense>
          </PageTransition>
        </main>
      </div>
    );
  }

  return (
    <div className="flex bg-background h-screen supports-[height:100dvh]:h-[100dvh] overflow-hidden">
      <LicorneDecor actif={licorne} prenom={prenom} bandeau={false} />
      {/* Barre latérale — bureau uniquement ; sur mobile, barre d'onglets + feuille « Plus ». */}
      <aside className="hidden lg:flex w-[240px] flex-shrink-0 flex-col h-full bg-card border-r border-border">
        <div className="flex items-center gap-2.5 px-5 py-[18px] border-b border-muted">
          <BrandMark />
          <div className="min-w-0">
            <span className="block text-[15px] font-extrabold tracking-[-0.02em]">CRM Webforce Group</span>
            <span className="block text-xs font-semibold text-foreground-secondary">Webforce · Marketing & Sales</span>
          </div>
        </div>

        <nav className="flex-1 overflow-y-auto px-3 pt-3 pb-4" aria-label="Navigation principale">
          {sections.map(({ title, items }) => (
            <React.Fragment key={title}>
              <p className="mx-3 mt-3 mb-1.5 text-[11px] font-bold uppercase tracking-[0.08em] text-foreground-tertiary">{title}</p>
              {items.map(({ label, path, icon: Icon, tone }) => {
                const active = currentNav?.path === path;
                return (
                  <Link
                    key={path}
                    to={path}
                    aria-current={active ? 'page' : undefined}
                    className={`flex items-center gap-2.5 min-h-[40px] px-3 rounded-[10px] text-sm transition-colors ${
                      active ? 'bg-primary-soft text-primary-text font-extrabold' : 'text-foreground font-semibold hover:bg-muted'
                    }`}
                  >
                    <Icon className="w-4 h-4 flex-shrink-0" strokeWidth={2} style={{ color: tone }} aria-hidden="true" />
                    <span className="truncate">{label}</span>
                  </Link>
                );
              })}
            </React.Fragment>
          ))}
        </nav>
        <Nouveautes />
      </aside>

      <div className="flex-1 flex flex-col overflow-hidden min-w-0">
        {/* Barre du haut — 60 px, opaque (pas de backdrop-filter : il faisait scintiller la barre). */}
        <header className="sticky top-0 z-30 flex-shrink-0 bg-card border-b border-border safe-area-pt">
          <div className="flex items-center gap-3 h-[60px] px-4 sm:px-6">
            <span className="lg:hidden"><BrandMark size={28} /></span>
            <span className="text-[17px] font-extrabold tracking-[-0.02em] truncate">
              {currentNav?.label || TITRES_HORS_MENU[location.pathname] || 'Dashboard'}
            </span>

            <div className="ml-auto flex items-center gap-2 flex-shrink-0">
              {loading && !isSyncing && (
                <span className="tag tag-neutral">
                  <RefreshCw className="w-3 h-3 animate-spin" aria-hidden="true" />
                  <span className="hidden sm:inline">Chargement</span>
                </span>
              )}
              {/* Ordre FIXE, dicté par BOARDS_TOPBAR et non par la base : le
                  `select` sur sync_status n'a pas d'ORDER BY, et chaque synchro
                  déplace sa ligne. Les pastilles changeaient de place plusieurs
                  fois par heure — la barre semblait clignoter. */}
              <div className={voitSynchro ? 'hidden lg:flex items-center gap-2' : 'hidden'}>
                {BOARDS_TOPBAR.map((boardKey) => {
                  const s = syncStatuses.find((row) => row.board_key === boardKey);
                  if (!s) return null;
                  return (
                    <span key={boardKey} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-border text-xs font-bold text-foreground-secondary whitespace-nowrap capitalize">
                      <span className={`w-1.5 h-1.5 rounded-full ${DOT_TONE[boardStatus(s)]}`} aria-hidden="true" />
                      {boardKey}
                    </span>
                  );
                })}
              </div>
              <UserMenu user={user} isAdmin={isAdmin} initials={userInitials} onLogout={logout} className="ml-1" />
            </div>
          </div>
        </header>

        {/* Contenu — le bas dégage la barre d'onglets mobile (64 px + safe-area). */}
        <main className="flex-1 overflow-y-auto min-h-0 overscroll-contain pb-[calc(88px+env(safe-area-inset-bottom))] lg:pb-0">
          <PageTransition>
            <Suspense fallback={<div className="skeleton h-28 rounded-xl" aria-busy="true" />}><Outlet /></Suspense>
          </PageTransition>
        </main>
      </div>

      <MobileTabBar onMore={() => setMoreSheetOpen(true)} moreActive={moreSheetOpen} canSee={canSeeNavItem} permitted={isAdmin ? null : permittedPages} />
      <MobileMoreSheet
        open={moreSheetOpen}
        onClose={() => setMoreSheetOpen(false)}
        sections={sections}
        sync={voitSynchro ? sync : null}
      />
    </div>
  );
}
